/**
 * Phase 640 — Video Analysis Studio page.
 * Handles BOTH create-new (route /video-analysis/new) and
 * edit-existing (/video-analysis/:id). On new, first step is picking
 * a student and uploading a video; then the annotation studio mounts.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Upload, Save, Share2, ArrowLeft, CheckCircle2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import VideoAnnotationStudio from '@/components/video-analysis/VideoAnnotationStudio';

const MAX_MB = 50; // Client-side soft cap; backend enforces 100MB.

export default function VideoAnalysisStudioPage() {
  const { id: analysisId } = useParams();
  const navigate = useNavigate();
  const isEdit = analysisId && analysisId !== 'new';
  const fileRef = useRef(null);

  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState('');
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [annotations, setAnnotations] = useState([]);
  const [videoSrc, setVideoSrc] = useState('');
  const [uploaded, setUploaded] = useState(null); // { storage_path, size_mb, mime_type }
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [loading, setLoading] = useState(isEdit);

  // Fetch students for the picker
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/students');
        setStudents(Array.isArray(r.data) ? r.data : (r.data?.rows || []));
      } catch { /* fail silent — user will see empty picker */ }
    })();
  }, []);

  // Fetch existing analysis in edit mode
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/video-analyses/${analysisId}`);
        const d = r.data;
        setTitle(d.title || '');
        setComment(d.comment || '');
        setAnnotations(d.annotations || []);
        setStudentId(d.student_id || '');
        setUploaded({ storage_path: d.storage_path, mime_type: d.mime_type });
        // For playback in edit mode, stream through the auth-gated URL with query auth
        const token = localStorage.getItem('outplay_token') || localStorage.getItem('token') || '';
        const base = process.env.REACT_APP_BACKEND_URL || '';
        setVideoSrc(`${base}/api/video-analyses/${analysisId}/stream${token ? `?auth=${encodeURIComponent(token)}` : ''}`);
        if (d.share_token) {
          setShareUrl(`${window.location.origin}/share/video/${d.share_token}`);
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to load analysis');
        navigate('/video-analysis');
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, analysisId, navigate]);

  const handleFilePick = useCallback(async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`File is ${(f.size / 1024 / 1024).toFixed(1)} MB. Please compress below ${MAX_MB} MB — most phones have a compress option in the share menu.`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await api.post('/video-analyses/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploaded(r.data);
      // Preview locally via object URL until saved
      setVideoSrc(URL.createObjectURL(f));
      if (!title) setTitle(f.name.replace(/\.[^.]+$/, '').slice(0, 60));
      toast.success(`Uploaded ${r.data.size_mb} MB — draw your annotations now.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [title]);

  const handleSave = useCallback(async () => {
    if (!studentId) { toast.error('Pick a student first'); return; }
    if (!uploaded && !isEdit) { toast.error('Upload a video first'); return; }
    if (!title.trim()) { toast.error('Give your clip a title'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/video-analyses/${analysisId}`, {
          title, comment, annotations,
        });
        toast.success('Saved');
      } else {
        const r = await api.post('/video-analyses', {
          title, comment, student_id: studentId,
          storage_path: uploaded.storage_path,
          mime_type: uploaded.mime_type,
          annotations,
        });
        toast.success('Analysis saved');
        navigate(`/video-analysis/${r.data.id}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [studentId, uploaded, isEdit, title, comment, annotations, analysisId, navigate]);

  const handleShare = useCallback(async () => {
    if (!isEdit) { toast.error('Save your analysis first'); return; }
    setSharing(true);
    try {
      const r = await api.post(`/video-analyses/${analysisId}/share`);
      setShareUrl(r.data.share_url);
      const parts = [];
      if (r.data.delivery?.whatsapp === 'sent') parts.push('WhatsApp sent');
      if (r.data.delivery?.email === 'queued') parts.push('email queued');
      toast.success(`Shared with parent · ${parts.join(' · ') || 'link ready'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Share failed');
    } finally {
      setSharing(false);
    }
  }, [isEdit, analysisId]);

  const copyShareUrl = useCallback(() => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl);
    toast.success('Share link copied — paste it wherever you want');
  }, [shareUrl]);

  if (loading) return <p className="p-4 text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="video-analysis-studio-page">
      <div className="flex items-center gap-2">
        <Link to="/video-analysis"><Button variant="ghost" size="sm" data-testid="back-to-list-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button></Link>
        <h1 className="text-xl font-display font-semibold flex-1">
          {isEdit ? 'Edit analysis' : 'New video analysis'}
        </h1>
        <Button onClick={handleSave} disabled={saving} data-testid="save-analysis-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
          Save
        </Button>
        {isEdit && (
          <Button onClick={handleShare} disabled={sharing} variant="default"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="share-analysis-btn">
            {sharing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Share2 className="h-4 w-4 mr-1" />}
            Share with parent
          </Button>
        )}
      </div>

      {shareUrl && (
        <Card className="border-emerald-200 bg-emerald-50/50" data-testid="share-url-card">
          <CardContent className="p-3 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
            <span className="text-xs font-mono truncate flex-1">{shareUrl}</span>
            <Button size="sm" variant="ghost" onClick={copyShareUrl} className="h-7 text-xs"
                    data-testid="copy-share-url">
              <Copy className="h-3 w-3 mr-1" /> Copy
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3">
          {!videoSrc && !isEdit ? (
            <Card className="border-dashed" data-testid="upload-drop">
              <CardContent className="p-8 text-center">
                <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                <p className="font-medium">Upload a video clip</p>
                <p className="text-xs text-muted-foreground mt-1">mp4, mov or webm · up to {MAX_MB} MB</p>
                <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/webm"
                       onChange={handleFilePick} className="hidden"
                       data-testid="video-file-input" />
                <Button className="mt-4" onClick={() => fileRef.current?.click()}
                        disabled={uploading} data-testid="video-upload-btn">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
                  {uploading ? 'Uploading…' : 'Choose file'}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <VideoAnnotationStudio
              videoSrc={videoSrc}
              annotations={annotations}
              onChange={setAnnotations}
            />
          )}
        </div>

        <div className="space-y-3">
          <div>
            <Label htmlFor="student-picker" className="text-xs">Student *</Label>
            <Select value={studentId} onValueChange={setStudentId} disabled={isEdit}>
              <SelectTrigger id="student-picker" data-testid="student-picker">
                <SelectValue placeholder="Pick a student…" />
              </SelectTrigger>
              <SelectContent>
                {students.map(s => (
                  <SelectItem key={s.id} value={s.id} data-testid={`student-opt-${s.id}`}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="analysis-title" className="text-xs">Title *</Label>
            <Input id="analysis-title" value={title} onChange={e => setTitle(e.target.value)}
                   placeholder="e.g. Shooting form correction"
                   data-testid="analysis-title-input" />
          </div>
          <div>
            <Label htmlFor="analysis-comment" className="text-xs">Coach&apos;s note</Label>
            <Textarea id="analysis-comment" value={comment} onChange={e => setComment(e.target.value)}
                      rows={5} placeholder="What should the player and parent focus on?"
                      data-testid="analysis-comment-input" />
          </div>
          <p className="text-[10px] text-muted-foreground leading-relaxed">
            Once you save, hit <b>Share with parent</b> to auto-send a WhatsApp and email with a private link.
            Parents don&apos;t need an app — the link opens in any browser.
          </p>
        </div>
      </div>
    </div>
  );
}
